import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router';
import {
  FRAMEWORKS,
  type FrameworkId,
  type DeliverableType,
  type DeliverableExportStatus,
} from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';

interface Deliverable {
  id: string;
  name: string;
  frameworkId: FrameworkId;
  type: DeliverableType;
  completedFields: number;
  totalFields: number;
  exportStatus: DeliverableExportStatus;
}

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'policy', label: 'Policy' },
  { value: 'process', label: 'Process' },
  { value: 'register', label: 'Register' },
  { value: 'evidence', label: 'Evidence' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'locked', label: 'Locked' },
  { value: 'unlockable', label: 'Unlockable' },
  { value: 'exportable', label: 'Exportable' },
];

export function DeliverableList() {
  const { currentWorkspace } = useWorkspace();
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterFramework, setFilterFramework] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchDeliverables();
  }, [currentWorkspace]);

  async function fetchDeliverables() {
    if (!currentWorkspace) return;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<Deliverable[]>(
      `/api/tenants/${currentWorkspace.tenantId}/deliverables`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setDeliverables(data);
    }
    setLoading(false);
  }

  // Build framework filter options from deliverables
  const frameworkOptions = useMemo(() => {
    const ids = new Set(deliverables.map((d) => d.frameworkId));
    const opts = [{ value: '', label: 'All frameworks' }];
    for (const id of ids) {
      const fw = FRAMEWORKS[id];
      if (fw) {
        opts.push({ value: id, label: fw.shortName });
      }
    }
    return opts;
  }, [deliverables]);

  // Filter deliverables
  const filtered = useMemo(() => {
    return deliverables.filter((d) => {
      if (filterFramework && d.frameworkId !== filterFramework) return false;
      if (filterType && d.type !== filterType) return false;
      if (filterStatus && d.exportStatus !== filterStatus) return false;
      return true;
    });
  }, [deliverables, filterFramework, filterType, filterStatus]);

  const typeBadgeVariant = (type: DeliverableType) => {
    switch (type) {
      case 'policy': return 'brand' as const;
      case 'process': return 'info' as const;
      case 'register': return 'warning' as const;
      case 'evidence': return 'success' as const;
      default: return 'default' as const;
    }
  };

  const exportStatusDisplay = (status: DeliverableExportStatus) => {
    switch (status) {
      case 'locked':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            Locked
          </span>
        );
      case 'unlockable':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-yellow-400">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 019.9-1" />
            </svg>
            Unlockable
          </span>
        );
      case 'exportable':
        return (
          <span className="inline-flex items-center gap-1 text-xs text-growth">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20,6 9,17 4,12" />
            </svg>
            Exportable
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-white">Deliverables</h1>
        <p className="text-slate-400 text-sm mt-1">
          {deliverables.length} total deliverable{deliverables.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-surface-mid rounded-xl border border-border p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Select
            label="Framework"
            options={frameworkOptions}
            value={filterFramework}
            onChange={(e) => setFilterFramework(e.target.value)}
          />
          <Select
            label="Type"
            options={TYPE_OPTIONS}
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          />
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          />
        </div>
      </div>

      {/* Deliverable list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-400 text-sm">
            {deliverables.length === 0
              ? 'No deliverables yet. Activate frameworks and complete assessments to generate deliverables.'
              : 'No deliverables match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((del) => {
            const fw = FRAMEWORKS[del.frameworkId];
            const pct = del.totalFields > 0
              ? Math.round((del.completedFields / del.totalFields) * 100)
              : 0;

            return (
              <Link
                key={del.id}
                to={`/deliverables/${del.id}`}
                className="flex items-center gap-4 bg-surface-mid rounded-lg border border-border p-4 hover:border-slate-600 transition-colors"
              >
                {/* Name and framework */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{del.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {fw && (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{ backgroundColor: fw.color }}
                        />
                        {fw.shortName}
                      </span>
                    )}
                    <Badge variant={typeBadgeVariant(del.type)}>{del.type}</Badge>
                  </div>
                </div>

                {/* Completion bar */}
                <div className="w-28 shrink-0">
                  <div className="h-1.5 bg-surface-dark rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 text-right mt-1">
                    {del.completedFields}/{del.totalFields}
                  </p>
                </div>

                {/* Export status */}
                <div className="w-24 shrink-0 text-right">
                  {exportStatusDisplay(del.exportStatus)}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
